import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";

// The tutors a /find-a-tutor/<language> page shows (#1644): up to three a visitor can book, who have
// a public page, bookable first then newest (#1096). The server decides who they are, so the page
// copies no rule. The server render reads them with exactly this query
// (LanguagePagesController::LANGUAGE_PAGE_TUTORS_QUERY, pinned by
// spec/requests/language_pages_tutors_spec.rb) and seeds the answer under this key.
export const LANGUAGE_PAGE_TUTORS_QUERY = graphql(`
  query LanguagePageTutors($language: String!) {
    languagePageTutors(language: $language) {
      id
      slug
      profileTitle
      hourlyRateCents
      averageRating
      reviewCount
      user {
        id
        fullName
        initials
      }
    }
  }
`);

// The photos come with links that expire (MediaUrls), so they are not in the server render, which is
// cached: the browser asks for them once the page is up, and the initials show meanwhile.
const LANGUAGE_PAGE_TUTOR_PHOTOS_QUERY = graphql(`
  query LanguagePageTutorPhotos($language: String!) {
    languagePageTutors(language: $language) {
      id
      user {
        id
        avatarUrl
      }
    }
  }
`);

export const languagePageTutorsKey = (code: string | undefined) => ["languagePageTutors", code] as const;

export const useLanguagePageTutors = (code: string | undefined) =>
  useQuery({
    queryKey: languagePageTutorsKey(code),
    queryFn: async () =>
      (await graphqlClient.request(LANGUAGE_PAGE_TUTORS_QUERY, { language: code ?? "" })).languagePageTutors,
    enabled: Boolean(code),
    staleTime: 1000 * 60,
  });

// tutor id -> photo link, once the page has mounted (never in the server render).
export const useLanguagePageTutorPhotos = (code: string | undefined, enabled: boolean) => {
  const { data } = useQuery({
    queryKey: ["languagePageTutorPhotos", code],
    queryFn: async () =>
      (await graphqlClient.request(LANGUAGE_PAGE_TUTOR_PHOTOS_QUERY, { language: code ?? "" })).languagePageTutors,
    enabled: Boolean(code) && enabled,
    staleTime: 1000 * 60 * 5,
  });
  return new Map((data ?? []).map((tutor) => [tutor.id, tutor.user.avatarUrl] as const));
};

// True once the component is in the browser: what gates the photo request.
export const useMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
};
